import React, { useState } from 'react'
import { FormattedMessage, FormattedDate } from 'react-intl'
import { css, cx } from '@emotion/css'
import {
  Dialog,
  DialogBody,
  DialogFooter
} from '../../legacy-design-system/modal'
import { FileInput } from '../../legacy-design-system/form-file'
import { LoadingIndicatorButton } from '../../design-system'
import { metadataCss } from '../../legacy-design-system/text'
import {
  StigTemplateDetailsQuery,
  StigTemplateDetails,
  PersistStigTemplateMutation
} from './disa-stig-modal-queries'
import { downloadTemplate } from './disa-stig-template-downloader'
import { ContrastFormattedMessage } from '../contrast-formatted-message'
import { Link } from '@contrast-security-inc/design-system-typography'
import { Button } from '@contrast-security-inc/design-system-buttons'

interface StigModalProps {
  readonly isOpen: boolean
  readonly closeModal: () => void
  readonly organizationID: string
  readonly applicationID: string
}

export function StigModal({
  isOpen,
  closeModal,
  organizationID,
  applicationID
}: StigModalProps) {
  return (
    <Dialog
      isOpen={isOpen}
      onCancel={closeModal}
      renderTitle={() => <FormattedMessage id="title_stigViewerChecklist" />}
      size="small"
    >
      <StigTemplateDetailsQuery
        organizationID={organizationID}
        applicationID={applicationID}
      >
        {({ details }) => {
          return (
            <StigModalForm
              organizationID={organizationID}
              applicationID={applicationID}
              existingTemplate={details}
              closeModal={closeModal}
            />
          )
        }}
      </StigTemplateDetailsQuery>
    </Dialog>
  )
}

interface StigModalFormProps {
  readonly organizationID: string
  readonly applicationID: string
  readonly existingTemplate?: StigTemplateDetails
  readonly closeModal: () => void
}

function StigModalForm({
  organizationID,
  applicationID,
  existingTemplate,
  closeModal
}: StigModalFormProps) {
  const [stigFile, setStigFile] = useState<File>()
  const [uploadError, setUploadError] = useState<string>()
  const [showFileInput, setShowFileInput] = useState(!existingTemplate)
  const [downloadInProgress, setDownloadInProgress] = useState(false)
  return (
    <PersistStigTemplateMutation>
      {({ persistStigTemplate }) => {
        return (
          <React.Fragment>
            <DialogBody>
              <FormattedMessage
                id={
                  !!existingTemplate
                    ? 'details_stigViewerChecklist'
                    : 'details_stigViewerChecklist_upload'
                }
              >
                {(text) => {
                  return (
                    <div
                      className={css`
                        padding-bottom: 16px;
                      `}
                    >
                      {text}
                    </div>
                  )
                }}
              </FormattedMessage>
              {!showFileInput && !!existingTemplate && (
                <FormattedDate
                  value={existingTemplate.created}
                  {...{
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  }}
                >
                  {(createdText) => {
                    return (
                      <FormattedMessage
                        id="variable_stig_lastImported"
                        values={{ lastImported: createdText }}
                      >
                        {(lastImportedText) => {
                          return (
                            <FormattedMessage id="action_stigViewerChecklist_import">
                              {(importText) => {
                                return (
                                  <React.Fragment>
                                    <span
                                      className={cx(
                                        metadataCss,
                                        css`
                                          padding-right: 6px;
                                        `
                                      )}
                                    >
                                      {lastImportedText}
                                    </span>
                                    <Link
                                      className={css`
                                        font-size: 12px;
                                      `}
                                      onClick={() => setShowFileInput(true)}
                                    >
                                      {importText}
                                    </Link>
                                  </React.Fragment>
                                )
                              }}
                            </FormattedMessage>
                          )
                        }}
                      </FormattedMessage>
                    )
                  }}
                </FormattedDate>
              )}
              {showFileInput && (
                <FormattedMessage id="action_stigViewerChecklist_import">
                  {(label) => {
                    return (
                      <ContrastFormattedMessage id="action_stigViewerChecklist_select">
                        {(placeholder) => {
                          return (
                            <ContrastFormattedMessage id="details_stigViewerChecklist_error_invalidTemplate">
                              {(emptyContentErrorMessage) => {
                                return (
                                  <FileInput
                                    id="stig-template"
                                    label={label as any}
                                    placeholder={placeholder}
                                    accept=".csv, .ckl"
                                    file={stigFile}
                                    errorMessage={uploadError}
                                    onChange={(fileList) => {
                                      const file = fileList[0]
                                      const name = file.name
                                      const nameSplit = name.split('.')
                                      const fileType =
                                        nameSplit[
                                          nameSplit.length - 1
                                        ].toUpperCase()

                                      // eslint-disable-next-line @typescript-eslint/no-floating-promises
                                      loadFileAsString(file).then((content) => {
                                        if (content.length < 1) {
                                          setUploadError(
                                            emptyContentErrorMessage
                                          )
                                        } else {
                                          setUploadError(undefined)
                                          return persistStigTemplate({
                                            organizationID,
                                            applicationID,
                                            name,
                                            fileType,
                                            content
                                          }).then(() => {
                                            setStigFile(file)
                                          })
                                        }
                                      })
                                    }}
                                  />
                                )
                              }}
                            </ContrastFormattedMessage>
                          )
                        }}
                      </ContrastFormattedMessage>
                    )
                  }}
                </FormattedMessage>
              )}
            </DialogBody>
            <DialogFooter>
              <FormattedMessage id="action_cancel">
                {(text) => (
                  <Button
                    className={css`
                      height: 34px;
                      font-size: 14px;
                    `}
                    variant="secondary"
                    onClick={closeModal}
                  >
                    {text}
                  </Button>
                )}
              </FormattedMessage>

              <FormattedMessage id="action_applicationReports_generate">
                {(text) => {
                  return (
                    <LoadingIndicatorButton
                      className={css`
                        margin-left: 8px;
                      `}
                      disabled={!existingTemplate && !stigFile}
                      loading={downloadInProgress}
                      onClick={(e) => {
                        e.preventDefault()
                        setDownloadInProgress(true)
                        downloadTemplate({
                          organizationID,
                          applicationID
                        })
                          .then(closeModal)
                          .finally(() => setDownloadInProgress(false))
                      }}
                    >
                      {text}
                    </LoadingIndicatorButton>
                  )
                }}
              </FormattedMessage>
            </DialogFooter>
          </React.Fragment>
        )
      }}
    </PersistStigTemplateMutation>
  )
}

function loadFileAsString(file: File): Promise<string> {
  const reader = new FileReader()

  return new Promise((resolve, reject) => {
    reader.onerror = () => {
      reject()
    }
    reader.onload = () => {
      resolve(reader.result as string)
    }
    reader.readAsText(file, 'UTF-8')
  })
}
